import React, { useEffect, useRef, useState } from 'react';
import { Copy, Flag, MousePointer, Play, Scissors, SlidersHorizontal } from 'lucide-react';
import coachCopy from '@/data/classCoach.json';
import { CLASS_GROUP_META, CLASS_GROUPS } from '@/state/classSession';
import { pickCounts } from '@/state/classCoach';

const ToolIcon = ({ step }) => {
  if (step.tool?.id === 'Copy') return <Copy size={15} aria-hidden="true" />;
  if (step.tool?.id === 'Slice') return <Scissors size={15} aria-hidden="true" />;
  if (step.tool?.id === 'Select') return <MousePointer size={15} aria-hidden="true" />;
  if (step.showMe === 'markers') return <Flag size={15} aria-hidden="true" />;
  if (step.showMe === 'hook' || step.showMe === 'listen') return <Play size={15} aria-hidden="true" />;
  return <SlidersHorizontal size={15} aria-hidden="true" />;
};

const HIDDEN_KEY = 'ba-class-coach-hidden';
const readHidden = () => { try { return window.localStorage.getItem(HIDDEN_KEY) === '1'; } catch (error) { return false; } };
const writeHidden = (value) => { try { window.localStorage.setItem(HIDDEN_KEY, value ? '1' : '0'); } catch (error) { /* storage blocked: still hides for this visit */ } };

export function ClassCoach({ phase, flow = 1, tracks = [], stepIndex = 0, skipped = [], event = null, notice = null,
  flyMeter = null, showcaseText = null, onDismiss, onShowMe, onSkip }) {
  const [viewingStep, setViewingStep] = useState(stepIndex);
  const [confirmSkip, setConfirmSkip] = useState(false);
  // Kids or teachers can tuck the step tips away for more grid room. "Hold up fam" warnings
  // still show; friendly cheers stay quiet while tips are hidden.
  const [hidden, setHiddenState] = useState(readHidden);
  const setHidden = (value) => { setHiddenState(value); writeHidden(value); };
  const previousFurthest = useRef(stepIndex);
  useEffect(() => {
    if (stepIndex > previousFurthest.current) setViewingStep(stepIndex);
    previousFurthest.current = stepIndex;
  }, [stepIndex]);
  useEffect(() => setConfirmSkip(false), [viewingStep, stepIndex]);
  const overlay = event || notice;
  const quietWhileHidden = hidden && phase !== 'pick' && overlay && ['cheer', 'tool-cheer', 'flowers'].includes(overlay.kind);
  if (overlay && !quietWhileHidden) return <section className="class-coach class-coach-alert" data-testid="class-coach-alert" role="status">
    <img src={`/pizzul/${overlay.pose || 'thinking'}.png`} alt="Pizzul" />
    <div><strong>{overlay.kind === 'flowers' ? 'That is it' : ['cheer', 'tool-cheer'].includes(overlay.kind) ? 'Nice' : 'Hold up fam'}</strong><p>{overlay.line}</p></div>
    <div className="class-coach-actions">{coachCopy.dismissLabels.map((label) => <button key={label} onClick={onDismiss}>{label}</button>)}</div>
  </section>;

  if (phase === 'pick') {
    const counts = pickCounts(tracks);
    return <section className="class-coach" data-testid="class-pick-coach" role="status">
      <img src={`/pizzul/${coachCopy.pick.pose}.png`} alt="Pizzul" />
      <div><p>{coachCopy.pick.line}</p><div className="class-coach-counts">{CLASS_GROUPS.map((group) => <span key={group} style={{ '--class-group-color': CLASS_GROUP_META[group].color }}>
        {CLASS_GROUP_META[group].label}: {counts[group]}
      </span>)}</div></div>
    </section>;
  }

  const steps = flow === 2 ? coachCopy.steps : coachCopy.stepsV1;
  const shownIndex = flow === 2 ? viewingStep : stepIndex;
  const step = steps[Math.max(0, Math.min(shownIndex, steps.length - 1))];
  if (!step) return null;
  if (hidden) return <section className="class-coach class-coach-hidden" data-testid="class-coach-hidden" role="status">
    <span>Step {stepIndex + 1} of {steps.length}: {steps[Math.min(stepIndex, steps.length - 1)].title}</span>
    <button data-testid="class-coach-show" onClick={() => setHidden(false)}>Show tips</button>
  </section>;
  const hideButton = <button data-testid="class-coach-hide" className="class-coach-hide" onClick={() => setHidden(true)}>Hide tips</button>;
  if (flow !== 2) return <section className="class-coach" data-testid="class-step-coach" role="status">
    <img src={`/pizzul/${step.pose}.png`} alt="Pizzul" />
    <div className="class-coach-step-copy"><span>Step {stepIndex + 1} of {steps.length}: {step.title}</span><p>{step.line}</p></div>
    <div className="class-coach-actions"><button data-testid="class-show-me" onClick={() => onShowMe?.(step)}>Show me</button><button data-testid="class-skip-step" onClick={() => onSkip?.(stepIndex)}>Skip</button>{hideButton}</div>
  </section>;

  const skippedSet = new Set(skipped);
  const hasDrums = tracks.some((track) => !track.inBank && track.instrument === 'drums');
  const line = step.id === 'intro' && !hasDrums ? step.altLine : step.line;
  const lookingBack = shownIndex < stepIndex;
  return <section className="class-coach" data-testid="class-step-coach" role="status">
    <img src={`/pizzul/${step.pose}.png`} alt="Pizzul" />
    <div className="class-coach-step-copy">
      <span>Step {shownIndex + 1} of {steps.length}: {step.title}</span>
      {lookingBack && <p className="class-looking-back">You&apos;re looking back at {step.title}.</p>}
      {!lookingBack && showcaseText && step.id === 'listen' ? <p data-testid="class-showcase-text">{showcaseText}</p> : <p>{line}</p>}
      <div className="class-tool-chip" data-testid="class-tool-chip"><ToolIcon step={step} /><strong>{step.tool?.label}</strong><span>{step.tool?.tip}</span></div>
      <div className="class-step-dots" role="list" aria-label="Arrange steps">{steps.map((item, index) => {
        const isSkipped = skippedSet.has(item.id);
        const enabled = index <= stepIndex || isSkipped;
        return <button key={item.id} type="button" role="listitem" data-testid={`class-step-dot-${index + 1}`} className={`${index === shownIndex ? 'current' : ''}${isSkipped ? ' skipped' : ''}`}
          disabled={!enabled} aria-current={index === shownIndex ? 'step' : undefined} aria-label={`Step ${index + 1}, ${item.title}${isSkipped ? ', skipped' : ''}`}
          onClick={() => enabled && setViewingStep(index)}><b>{index + 1}</b>{isSkipped && <small aria-hidden="true">•</small>}</button>;
      })}</div>
      {step.id === 'fly' && shownIndex === stepIndex && flyMeter && <div className="class-fly-meter" data-testid="class-fly-meter"><span>{flyMeter.durationText}</span><span>{flyMeter.tracksText}</span></div>}
      {confirmSkip && <div className="class-skip-confirm" data-testid="class-skip-confirm"><span>Skip this step? You can come back to it.</span><button data-testid="class-skip-confirm-yes" onClick={() => { onSkip?.(shownIndex); setConfirmSkip(false); }}>Skip</button><button data-testid="class-skip-confirm-no" onClick={() => setConfirmSkip(false)}>Keep going</button></div>}
    </div>
    {/* While the skip confirm is open its two buttons are the only choices, so a kid never sees two Skips. */}
    {!confirmSkip && <div className="class-coach-actions class-step-actions">
      {shownIndex > 0 && <button data-testid="class-step-back" onClick={() => setViewingStep((index) => Math.max(0, index - 1))}>Back</button>}
      {shownIndex < stepIndex && <button data-testid="class-step-next" onClick={() => setViewingStep((index) => Math.min(stepIndex, index + 1))}>Next</button>}
      <button data-testid="class-show-me" onClick={() => onShowMe?.(step)}>Show me</button>
      {shownIndex === stepIndex && !(step.id === 'listen' && showcaseText) && <button data-testid="class-skip-step" onClick={() => setConfirmSkip(true)}>Skip</button>}
      {hideButton}
    </div>}
  </section>;
}

export default ClassCoach;
